import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Building2, ShieldCheck, Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { invitationsAPI } from '../lib/invitationsAPI';

export default function InvitationRegister() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [invitation, setInvitation] = useState(null);
  const [firm, setFirm] = useState(null);

  const [mobile, setMobile] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let active = true;
    invitationsAPI.getByToken(token)
      .then((res) => {
        if (!active) return;
        setInvitation(res.data.invitation);
        setFirm(res.data.firm);
        setMobile(res.data.invitation?.mobile || '');
      })
      .catch((err) => {
        if (!active) return;
        setError(err.response?.data?.detail || 'This registration link is invalid.');
      })
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [token]);

  const handleSubmit = async () => {
    if (password.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    if (password !== confirm) { toast.error('Passwords do not match'); return; }
    if (!consent) { toast.error('Please accept the terms and privacy consent'); return; }
    setSubmitting(true);
    try {
      const res = await invitationsAPI.register(token, { password, mobile, consent });
      // Auto-login into the client portal using sessionStorage for tab isolation
      sessionStorage.setItem('access_token', res.data.access_token);
      sessionStorage.setItem('user', JSON.stringify(res.data.user));
      sessionStorage.setItem('organization', JSON.stringify(res.data.organization));
      toast.success('Welcome! Let\'s get your onboarding started.');
      navigate('/onboarding');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registration failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/50">
        <Loader2 className="w-8 h-8 animate-spin text-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/50 p-6">
        <Card className="max-w-md w-full">
          <CardContent className="pt-10 pb-8 text-center">
            <AlertCircle className="w-14 h-14 mx-auto mb-4 text-amber-500" />
            <h2 className="text-xl font-bold text-foreground">Link unavailable</h2>
            <p className="text-muted-foreground mt-2">{error}</p>
            <div className="mt-6 flex flex-col items-center gap-2">
              <Button onClick={() => navigate('/login')} data-testid="invite-error-go-to-login">Go to login</Button>
              <Link to="/forgot-password" className="text-sm text-primary hover:underline" data-testid="invite-error-forgot-password">Forgot password?</Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted py-10 px-4">
      <div className="max-w-lg mx-auto">
        {/* Firm header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 rounded-2xl bg-primary text-white flex items-center justify-center mx-auto mb-3">
            <Building2 className="w-8 h-8" />
          </div>
          <h1 className="text-2xl font-bold text-foreground" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            {firm?.name || 'Client Portal'}
          </h1>
          <p className="text-muted-foreground text-sm">Client Portal Registration</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-xl text-foreground">
              Welcome{invitation?.first_name ? `, ${invitation.first_name}` : ''}!
            </CardTitle>
            <CardDescription>
              {invitation?.message
                ? `“${invitation.message}”`
                : 'Set up your secure portal access to begin onboarding.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Name</Label>
                <Input value={`${invitation?.first_name || ''} ${invitation?.last_name || ''}`.trim()} disabled />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input value={invitation?.email || ''} disabled />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Confirm your mobile number</Label>
              <Input value={mobile} onChange={(e) => setMobile(e.target.value)} placeholder="04xx xxx xxx" data-testid="reg-mobile" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Create a password *</Label>
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 8 characters" data-testid="reg-password" />
              </div>
              <div className="space-y-2">
                <Label>Confirm password *</Label>
                <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="reg-confirm" />
              </div>
            </div>

            <div className="flex items-start gap-3 bg-muted/50 border rounded-lg p-3">
              <Checkbox id="consent" checked={consent} onCheckedChange={(v) => setConsent(!!v)} data-testid="reg-consent" className="mt-0.5" />
              <Label htmlFor="consent" className="text-sm font-normal text-muted-foreground leading-relaxed cursor-pointer">
                I agree to {firm?.name || 'the firm'}'s terms of engagement and privacy policy, and consent to the
                secure handling of my personal and financial information.
              </Label>
            </div>

            <Button className="w-full" onClick={handleSubmit} disabled={submitting} data-testid="reg-submit">
              {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Setting up…</> : 'Start Onboarding'}
            </Button>

            <p className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground/70">
              <ShieldCheck className="w-3.5 h-3.5" /> Secured with a single-use, time-limited registration link.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
